import { useTranslation } from '@/contexts/i18n-context';
import { useGetEncountersInsurance } from '@/api/get-insurance-info-for-encounter';
import { Loader } from '@/components/loaders/loader';
import { DataRow, ExpandableInfo } from '@/components/encounter/review/expandable-info';
import { formatDate } from '@/util/formats';
import { useBranding } from '@/outlets/branding-outlet';
import { InsuranceEncounter } from '@/api/api.schemas';

const translationBase = 'visit.review';

interface InsuranceInfoSectionProps {
  encounterId: number;
  showEditButton?: boolean;
}

export function InsuranceInfoSection({ encounterId, showEditButton }: InsuranceInfoSectionProps) {
  const { t } = useTranslation();
  const { hasInsurance, usePatientIdNumberLabel } = useBranding();
  const {
    data: insurance,
    isFetching,
    error,
  } = useGetEncountersInsurance(encounterId, { query: { enabled: hasInsurance } });

  if (!hasInsurance) {
    return null;
  }

  return (
    <ExpandableInfo
      title={t(`${translationBase}.insurance.title`)}
      {...(showEditButton && {
        editProps: {
          editTo: `/visit/${encounterId}/insurance-information`,
          editModeParam: 'insurance',
        },
      })}
    >
      {isFetching && <Loader type="small" />}
      {error && <p>{t(`${translationBase}.insurance.error`)}</p>}
      {insurance?.insuranceEncounter ? (
        <>
          <DataRow title={t(`${translationBase}.insurance.relationship`)}>
            {insurance.insuranceEncounter.insuranceRelation}
          </DataRow>
          <DataRow title={t(`${translationBase}.insurance.subscriberName`)}>
            {insurance.insuranceEncounter.insuredFirstName &&
            insurance.insuranceEncounter.insuredLastName
              ? `${insurance.insuranceEncounter.insuredFirstName} ${insurance.insuranceEncounter.insuredLastName}`
              : t(`${translationBase}.none`)}
          </DataRow>
          <DataRow title={t(`${translationBase}.insurance.subscriberBirthday`)}>
            {insurance.insuranceEncounter.insuredBirthday
              ? formatDate(insurance.insuranceEncounter.insuredBirthday)
              : t(`${translationBase}.none`)}
          </DataRow>
          <DataRow title={t(`${translationBase}.insurance.subscriberAddress`)}>
            {formatAddress(insurance.insuranceEncounter)}
          </DataRow>
          <DataRow
            title={t(
              `${translationBase}.insurance.${usePatientIdNumberLabel ? 'insurancePatientId' : 'insuranceId'}`,
            )}
          >
            {insurance.insuranceEncounter.insuranceId}
          </DataRow>
          <DataRow title={t(`${translationBase}.insurance.insurer`)}>
            {insurance.insuranceEncounter.insurancePlanName}
          </DataRow>
        </>
      ) : (
        <p>{t(`${translationBase}.insurance.empty`)}</p>
      )}
    </ExpandableInfo>
  );
}

function formatAddress(insuranceEncounter: InsuranceEncounter) {
  return `${insuranceEncounter.insuredPrimaryAddr1} ${insuranceEncounter.insuredPrimaryAddr2 ? `${insuranceEncounter.insuredPrimaryAddr2}, ` : ''}${insuranceEncounter.insuredPrimaryCity}, ${insuranceEncounter.insuredPrimaryState} ${insuranceEncounter.insuredPrimaryZip}`;
}
